import { DATA_QUERY_TYPE, DataQueryType } from './data-query';

export const AI_CHAT_MESSAGE_ROLE = {
    USER: 'user',
    ASSISTANT: 'assistant',
    MANUAL: 'manual',
    SYSTEM: 'system',
} as const;

export type AiChatMessageRole = (typeof AI_CHAT_MESSAGE_ROLE)[keyof typeof AI_CHAT_MESSAGE_ROLE];

export const AI_CHAT_MESSAGE_KIND = {
    TEXT: 'text',
    FILTERS: 'filters',
    QUERY_RESULT: 'query_result',
    MANUAL_FILTERS: 'manual_filters',
    ERROR: 'error',
    DATA_COLLECTION_PICKER: 'data_collection_picker',
} as const;

export type AiChatMessageKind = (typeof AI_CHAT_MESSAGE_KIND)[keyof typeof AI_CHAT_MESSAGE_KIND];

export const INLINE_RESULT_QUERY_TYPES = new Set<string>([
    DATA_QUERY_TYPE.RANKING,
    DATA_QUERY_TYPE.AGGREGATION,
]);

export const rendersInlineResult = (queryType: unknown): queryType is DataQueryType =>
    typeof queryType === 'string' && INLINE_RESULT_QUERY_TYPES.has(queryType);

type MessageWithRole = {
    role?: AiChatMessageRole;
};

type MessageWithKind = {
    kind?: AiChatMessageKind;
};

export const isAiChatUserRole = (role: unknown): role is typeof AI_CHAT_MESSAGE_ROLE.USER =>
    role === AI_CHAT_MESSAGE_ROLE.USER;

export const isAiChatAssistantRole = (
    role: unknown
): role is typeof AI_CHAT_MESSAGE_ROLE.ASSISTANT => role === AI_CHAT_MESSAGE_ROLE.ASSISTANT;

export const isAiChatManualRole = (role: unknown): role is typeof AI_CHAT_MESSAGE_ROLE.MANUAL =>
    role === AI_CHAT_MESSAGE_ROLE.MANUAL;

export const isAiChatSystemRole = (role: unknown): role is typeof AI_CHAT_MESSAGE_ROLE.SYSTEM =>
    role === AI_CHAT_MESSAGE_ROLE.SYSTEM;

const hasAiChatMessageRole =
    <Role extends AiChatMessageRole>(isRole: (role: unknown) => role is Role) =>
    <Message extends MessageWithRole>(message: Message): message is Message & { role: Role } =>
        isRole(message.role);

export const isAiChatUserMessage = hasAiChatMessageRole(isAiChatUserRole);
export const isAiChatAssistantMessage = hasAiChatMessageRole(isAiChatAssistantRole);
export const isAiChatManualMessage = hasAiChatMessageRole(isAiChatManualRole);
export const isAiChatSystemMessage = hasAiChatMessageRole(isAiChatSystemRole);

const hasAiChatMessageKind =
    <Kind extends AiChatMessageKind>(kind: Kind) =>
    <Message extends MessageWithKind>(message: Message): message is Message & { kind: Kind } =>
        message.kind === kind;

export const isAiChatTextMessage = hasAiChatMessageKind(AI_CHAT_MESSAGE_KIND.TEXT);
export const isAiChatFiltersMessage = hasAiChatMessageKind(AI_CHAT_MESSAGE_KIND.FILTERS);
export const isAiChatQueryResultMessage = hasAiChatMessageKind(AI_CHAT_MESSAGE_KIND.QUERY_RESULT);
export const isAiChatManualFiltersMessage = hasAiChatMessageKind(
    AI_CHAT_MESSAGE_KIND.MANUAL_FILTERS
);
export const isAiChatErrorMessage = hasAiChatMessageKind(AI_CHAT_MESSAGE_KIND.ERROR);
export const isAiChatDataCollectionPickerMessage = hasAiChatMessageKind(
    AI_CHAT_MESSAGE_KIND.DATA_COLLECTION_PICKER
);
